import { useEffect, useState } from "react";
import FileSaver from "file-saver";
import { toast } from "react-toastify";

import { nameCsvFile, reportUnexpectedError } from "@graffer-inc/smart-apply";

import { StaticFeatureFlags } from "../../../../../../feature-flags";
import apiClient from "../../../../../../application/apis/api-client";
import { CloudGovApiError } from "../../../../../../application/domain/api-error";

type Props = {
  orgId: string;
};

export const useServiceProviderPaymentRawdataHooks = ({ orgId }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [terms, setTerms] = useState<string[]>([]);
  const [internalTerms, setInternalTerms] = useState<string[]>([]);
  const [targetYYYYMM, setTargetYYYYMM] = useState("");

  useEffect(() => {
    fetchTerms();
    //eslint-disable-next-line
  }, []);

  const fetchTerms = async () => {
    if (!StaticFeatureFlags.IS_SANDBOX) {
      try {
        const { data } = await apiClient.fetchPaymentRawdataTermsByOrgId(orgId);

        // fetchPaymentRawdataTermsByOrgIdは
        // テナント用の決済データ作成済みの期間を取得するAPIであるため、厳密には内部用データの作成済み期間は取得していない。
        // ただし両ファイルは同タイミングで作成されるため、ロジックがほぼ同一となる新規APIを作成するより
        // 実装量の少なさを優先し外部用が作成済みであれば内部用も存在するとみなす。
        if (data.length > 0) {
          setTerms(data);
          // 内部用決済データは2022/11分以降のみ作成
          setInternalTerms(data.filter((v) => v >= "202211"));
        }
      } catch (e) {
        if (!CloudGovApiError.is404(e)) {
          reportUnexpectedError(e);
        }
      }
    }
  };

  const onDownload = async (orgId: string, yyyymm: string) => {
    setIsLoading(true);

    try {
      const { data } = await apiClient.downloadPaymentRawdataByOrgId(
        orgId,
        yyyymm,
      );
      FileSaver.saveAs(data, nameCsvFile(`rawdata-${orgId}-${yyyymm}`));
    } catch (e) {
      if (CloudGovApiError.is404(e)) {
        toast.error("データがありません。");
        return;
      }
      toast.error("CSVダウンロードに失敗しました。");
      reportUnexpectedError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const onInternalDownload = async (orgId: string, yyyymm: string) => {
    setIsLoading(true);

    try {
      const { data } = await apiClient.downloadInternalPaymentRawdataByOrgId(
        orgId,
        yyyymm,
      );
      FileSaver.saveAs(
        data,
        nameCsvFile(`internal-rawdata-${orgId}-${yyyymm}`),
      );
    } catch (e) {
      if (CloudGovApiError.is404(e)) {
        toast.error("データがありません。");
        return;
      }
      toast.error("CSVダウンロードに失敗しました。");
      reportUnexpectedError(e);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    terms,
    internalTerms,
    targetYYYYMM,
    setTargetYYYYMM,
    onDownload,
    onInternalDownload,
  };
};
